import { useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { usePageBreadcrumbStore } from '../../store/pageBreadcrumbStore';

// Valori = CHIAVI i18n (tradotte al render con t()).
const BREADCRUMB_MAP: Record<string, string> = {
  dashboard: 'common.nav.dashboard',
  richieste: 'common.nav.richieste',
  ordini: 'common.nav.ordini',
  clienti: 'common.nav.clienti',
  produttori: 'common.nav.produttori',
  trasportatori: 'common.nav.trasportatori',
  intermediari: 'common.nav.intermediari',
  conferimenti: 'common.nav.conferimenti',
  scarichi: 'common.nav.scarichi',
  produzioni: 'common.nav.produzioni',
  fatturazione: 'common.nav.fatturazione',
  aree: 'common.nav.aree_stoccaggio',
  'documenti-impianto': 'common.nav.documenti_impianto',
  programmazione: 'common.nav.programmazione',
  settings: 'common.nav.settings',
  utenti: 'common.nav.utenti',
};

const ROLE_LABEL: Record<string, string> = {
  admin: 'common.ruoli.admin',
  AMM: 'common.ruoli.amm',
  COMMERCIALE: 'common.ruoli.commerciale',
  DIR: 'common.ruoli.dir',
  RT: 'common.ruoli.rt',
  OP: 'common.ruoli.op',
  OAP: 'common.ruoli.oap',
  COI: 'common.ruoli.coi',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

export default function TopBar() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);

  const { subLabel } = usePageBreadcrumbStore();

  const segments = location.pathname.split('/').filter(Boolean);
  const base = segments[0] ?? '';
  const subSegment = segments[1];
  const pageLabel = BREADCRUMB_MAP[base] ? t(BREADCRUMB_MAP[base]) : t('common.nav.pagina');
  const showSub = ['richieste', 'ordini'].includes(base) && !!subSegment;

  const initials = user?.full_name ? getInitials(user.full_name) : '??';
  const roleLabel = user ? (ROLE_LABEL[user.role] ? t(ROLE_LABEL[user.role]) : user.role) : '';

  return (
    <header className="sticky top-0 z-10 h-14 bg-white border-b border-stone-mid flex items-center px-6 gap-4 flex-shrink-0 shadow-sm">
      {/* Breadcrumb */}
      <div className="flex-1 flex items-center gap-2 text-sm">
        <NavLink to={`/${base}`} className="font-semibold text-ink-dark hover:text-sage transition-colors">
          {pageLabel}
        </NavLink>
        {showSub && subLabel && (
          <>
            <span className="text-ink-faint">/</span>
            <span data-testid="topbar-sublabel" className="font-mono text-sm font-semibold text-ink-dark">{subLabel}</span>
          </>
        )}
      </div>

      {/* Notifications */}
      <div className="relative">
        <button
          type="button"
          title={t('common.topbar.avvisi')}
          onClick={() => { setBellOpen((v) => !v); setMenuOpen(false); }}
          className="relative w-9 h-9 flex items-center justify-center rounded-lg text-ink-mid hover:bg-stone-bg transition-colors"
        >
          <Bell size={18} />
        </button>
        {bellOpen && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setBellOpen(false)} />
            <div className="absolute top-[calc(100%+6px)] right-0 w-72 bg-white border border-stone-mid rounded-lg shadow-lg py-2 z-30">
              <div className="px-3 pb-2 border-b border-stone-mid text-[11px] font-semibold text-ink-soft uppercase tracking-wide">
                {t('common.topbar.avvisi')}
              </div>
              <div className="px-3 py-4 text-center text-sm text-ink-faint italic">
                {t('common.topbar.nessun_avviso')}
              </div>
            </div>
          </>
        )}
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          type="button"
          data-testid="user-menu-button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-stone-bg transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center text-white text-xs font-semibold select-none">
            {initials}
          </div>
          <div className="text-left hidden sm:block">
            <div className="text-[13px] font-semibold text-ink-dark leading-tight">
              {user?.full_name ?? '—'}
            </div>
            <div className="text-[11px] text-ink-soft leading-tight">{roleLabel}</div>
          </div>
          <ChevronDown size={14} className="text-ink-soft" />
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
            <div className="absolute top-[calc(100%+6px)] right-0 min-w-[220px] bg-white border border-stone-mid rounded-lg shadow-lg py-1 z-30">
              <div className="px-3 py-2.5 border-b border-stone-mid">
                <div className="text-[13px] font-semibold text-ink-dark">
                  {user?.full_name}
                </div>
                <div className="text-[12px] text-ink-soft">{roleLabel}</div>
              </div>
              <button
                type="button"
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink-mid hover:bg-stone-bg transition-colors"
              >
                <UserIcon size={15} />
                <span>{t('common.topbar.il_mio_profilo')}</span>
              </button>
              <div className="h-px bg-stone-mid my-1" />
              <button
                type="button"
                data-testid="logout-button"
                onClick={() => { setMenuOpen(false); logout(); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-accent-danger hover:bg-accent-danger-bg transition-colors"
              >
                <LogOut size={15} />
                <span>{t('common.topbar.esci')}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
